import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Progress, Segmented, Skeleton } from 'antd';
import {
  ArrowRightOutlined,
  CalendarOutlined,
  CameraOutlined,
  CheckCircleFilled,
  DisconnectOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  FileTextOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { publicProjectService, PublicProject } from '../services/publicProjectService';
import { ShareLanguage, TaskStatus, TASK_STATUSES } from '../services/projectService';
import { EmptyState, FilterChips, PersonAvatar, Pill, Tone } from '../components/ui';
import { fileKind } from '../components/ui/fileKind';
import ImageLightbox, { LightboxImage } from '../components/ui/ImageLightbox';
import './ClientProjectPage.css';

type PhotoFilter = 'all' | 'before' | 'after';

const LANGUAGES: ShareLanguage[] = ['de', 'en', 'sq'];
const STATUS_TONE: Record<string, Tone> = { active: 'success', finished: 'primary', canceled: 'danger' };
const TEAM_DAYS_STEP = 10;

/** "2026-10-01" as a local date (new Date('YYYY-MM-DD') would be UTC midnight). */
const parseDay = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * The page a client opens from a link the admin sent (/p/<token>), no login. Always shows the
 * project's name, location, status and dates; plan, photos, documents and who worked when only
 * appear when the admin switched them on for this link (the API doesn't even send the rest).
 */
const ClientProjectPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<PublicProject | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');
  const [photoFilter, setPhotoFilter] = useState<PhotoFilter>('all');
  const [teamDaysShown, setTeamDaysShown] = useState(TEAM_DAYS_STEP);
  const [viewer, setViewer] = useState<{ images: LightboxImage[]; index: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    publicProjectService
      .get(token)
      .then((result) => {
        if (cancelled) return;
        // The language the admin picked for this client. Not saved to localStorage, so an admin
        // previewing the link in their own browser keeps their panel language.
        if (result.language && result.language !== i18n.language) i18n.changeLanguage(result.language);
        setData(result);
        setState('ready');
      })
      .catch((error) => {
        if (!cancelled) setState(error?.response?.status === 404 ? 'notFound' : 'error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (data) document.title = `${data.project.name} · Estethica22`;
  }, [data]);

  const formatDay = (value: string, withWeekday = false) =>
    parseDay(value).toLocaleDateString(i18n.language, {
      ...(withWeekday ? { weekday: 'long' } : {}),
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });

  const photos = useMemo(() => data?.photos ?? [], [data]);
  const photoCounts = useMemo(() => {
    const count = (type?: 'before' | 'after') =>
      photos.filter((post) => !type || post.type === type).reduce((sum, post) => sum + post.images.length, 0);
    return { all: count(), before: count('before'), after: count('after') };
  }, [photos]);

  /** Every visible photo flattened, newest post first — the grid and the viewer share the order. */
  const visiblePhotos = useMemo(
    () =>
      photos
        .filter((post) => photoFilter === 'all' || post.type === photoFilter)
        .flatMap((post) => post.images.map((image) => ({ ...image, type: post.type, created_at: post.created_at }))),
    [photos, photoFilter],
  );

  const header = (
    <header className="cpp-topbar">
      <img src="/estethica-logo.png" alt="Estethica22" className="cpp-logo" />
      <Segmented
        size="small"
        value={(LANGUAGES as string[]).includes(i18n.language) ? i18n.language : 'de'}
        options={LANGUAGES.map((value) => ({ value, label: value.toUpperCase() }))}
        onChange={(value) => i18n.changeLanguage(value as string)}
        aria-label={t('language.switchLanguage')}
      />
    </header>
  );

  if (state !== 'ready' || !data) {
    return (
      <div className="cpp">
        {header}
        <main className="cpp-main">
          {state === 'loading' ? (
            <div className="cpp-card">
              <Skeleton active paragraph={{ rows: 5 }} />
            </div>
          ) : (
            <div className="cpp-card cpp-missing">
              <EmptyState
                icon={<DisconnectOutlined />}
                text={state === 'notFound' ? t('client.notFound') : t('client.loadError')}
                action={
                  state === 'error' ? (
                    <Button onClick={() => window.location.reload()}>{t('client.retry')}</Button>
                  ) : undefined
                }
              />
            </div>
          )}
        </main>
      </div>
    );
  }

  const { project, sections, plan } = data;
  const showPhotos = sections.photos_before || sections.photos_after;
  const statusTone = STATUS_TONE[project.status] ?? 'neutral';
  const tasksBy = (status: TaskStatus) => (plan?.tasks ?? []).filter((task) => task.status === status);

  return (
    <div className="cpp">
      {header}
      <main className="cpp-main">
        {/* Basics: always shown */}
        <section className="cpp-card cpp-hero">
          <div className="cpp-hero-text">
            <Pill tone={statusTone}>{t(`projects.statuses.${project.status}`, project.status)}</Pill>
            <h1 className="cpp-name">{project.name}</h1>
            <a
              className="cpp-location"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(project.location)}`}
              target="_blank"
              rel="noreferrer"
            >
              <EnvironmentOutlined aria-hidden /> {project.location}
            </a>
            {(project.start_date || project.end_date) && (
              <div className="cpp-dates">
                <CalendarOutlined aria-hidden />
                <span>{project.start_date ? formatDay(project.start_date) : '—'}</span>
                <ArrowRightOutlined aria-hidden className="cpp-dates-arrow" />
                <span>{project.end_date ? formatDay(project.end_date) : '—'}</span>
              </div>
            )}
          </div>
          {plan && plan.tasks.length > 0 && (
            <div className="cpp-hero-progress">
              <Progress
                type="circle"
                percent={plan.done_percentage}
                size={104}
                strokeColor="#15803D"
                format={(percent) => (
                  <span className="cpp-progress-value">
                    {percent}%<small>{t('client.done')}</small>
                  </span>
                )}
              />
            </div>
          )}
        </section>

        {/* Plan board, read-only */}
        {sections.plan && plan && (
          <section className="cpp-card">
            <h2 className="cpp-section-title">
              <UnorderedListOutlined aria-hidden /> {t('client.plan')}
            </h2>
            {plan.tasks.length === 0 ? (
              <p className="cpp-muted">{t('client.noPlan')}</p>
            ) : (
              <>
                <div className="cpp-stack" aria-hidden>
                  <div className="cpp-stack-done" style={{ width: `${plan.done_percentage}%` }} />
                  <div
                    className="cpp-stack-progress"
                    style={{ width: `${tasksBy('in_progress').reduce((sum, task) => sum + task.percentage, 0)}%` }}
                  />
                </div>
                <div className="cpp-board">
                  {TASK_STATUSES.map((status) => {
                    const list = tasksBy(status);
                    return (
                      <div key={status} className={`cpp-column cpp-column--${status}`}>
                        <div className="cpp-column-head">
                          <span className="cpp-column-dot" aria-hidden />
                          {t(`projects.plan.columns.${status}`)}
                          <span className="cpp-column-count">{list.length}</span>
                        </div>
                        {list.length === 0 ? (
                          <div className="cpp-column-empty">—</div>
                        ) : (
                          list.map((task) => (
                            <div key={task.id} className="cpp-task">
                              <div className="cpp-task-title">
                                {status === 'done' && <CheckCircleFilled className="cpp-task-check" aria-hidden />}
                                {task.title}
                              </div>
                              {task.description ? <p className="cpp-task-description">{task.description}</p> : null}
                              <div className="cpp-task-share">{t('projects.plan.ofProject', { percent: task.percentage })}</div>
                            </div>
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        )}

        {/* Photos */}
        {showPhotos && (
          <section className="cpp-card">
            <h2 className="cpp-section-title">
              <CameraOutlined aria-hidden /> {t('client.photos')}
            </h2>
            {sections.photos_before && sections.photos_after && photoCounts.all > 0 && (
              <FilterChips<PhotoFilter>
                ariaLabel={t('client.photos')}
                value={photoFilter}
                onChange={setPhotoFilter}
                options={[
                  { key: 'all', label: t('client.allPhotos'), count: photoCounts.all },
                  { key: 'before', label: t('client.before'), count: photoCounts.before },
                  { key: 'after', label: t('client.after'), count: photoCounts.after },
                ]}
              />
            )}
            {visiblePhotos.length === 0 ? (
              <p className="cpp-muted">{t('client.noPhotos')}</p>
            ) : (
              <div className="cpp-photos">
                {visiblePhotos.map((image, index) => (
                  <button
                    key={image.id}
                    type="button"
                    className="cpp-photo"
                    onClick={() =>
                      setViewer({
                        images: visiblePhotos.map((item) => ({ full: item.url, thumb: item.thumbnail_url })),
                        index,
                      })
                    }
                  >
                    <img src={image.thumbnail_url} alt="" loading="lazy" />
                    <span className={`cpp-photo-badge cpp-photo-badge--${image.type}`}>
                      {t(image.type === 'before' ? 'client.before' : 'client.after')}
                    </span>
                    {image.created_at && <span className="cpp-photo-date">{formatDate(image.created_at)}</span>}
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Documents */}
        {sections.documents && (
          <section className="cpp-card">
            <h2 className="cpp-section-title">
              <FileTextOutlined aria-hidden /> {t('client.documents')}
            </h2>
            {(data.documents ?? []).length === 0 ? (
              <p className="cpp-muted">{t('client.noDocuments')}</p>
            ) : (
              <div className="cpp-docs">
                {(data.documents ?? []).map((doc) => {
                  const kind = fileKind(doc.url);
                  return (
                    <a key={doc.id} className="cpp-doc" href={doc.url} target="_blank" rel="noreferrer">
                      <span className="cpp-doc-icon" style={{ background: kind.soft, color: kind.color }} aria-hidden>
                        {kind.icon}
                      </span>
                      <span className="cpp-doc-text">
                        <span className="cpp-doc-name">{doc.name}</span>
                        <span className="cpp-doc-meta">
                          {kind.label}
                          {doc.created_at ? ` · ${formatDate(doc.created_at)}` : ''}
                        </span>
                      </span>
                      <ExportOutlined className="cpp-doc-open" aria-hidden />
                    </a>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Who worked on which day (no times) */}
        {sections.team_days && (
          <section className="cpp-card">
            <h2 className="cpp-section-title">
              <TeamOutlined aria-hidden /> {t('client.teamDays')}
            </h2>
            {(data.team_days ?? []).length === 0 ? (
              <p className="cpp-muted">{t('client.noTeamDays')}</p>
            ) : (
              <>
                <div className="cpp-days">
                  {(data.team_days ?? []).slice(0, teamDaysShown).map((day) => (
                    <div key={day.date} className="cpp-day">
                      <div className="cpp-day-date">{formatDay(day.date, true)}</div>
                      <div className="cpp-day-people">
                        {day.people.map((person, index) => {
                          const [first, ...rest] = person.name.split(' ');
                          return (
                            <span key={`${person.name}-${index}`} className="cpp-person">
                              <PersonAvatar name={first} surname={rest.join(' ')} imageUrl={person.image_url} size={26} />
                              {person.name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {(data.team_days ?? []).length > teamDaysShown && (
                  <Button block className="cpp-more" onClick={() => setTeamDaysShown((count) => count + TEAM_DAYS_STEP)}>
                    {t('client.showMoreDays')}
                  </Button>
                )}
              </>
            )}
          </section>
        )}

        <footer className="cpp-footer">Estethica22</footer>
      </main>

      {viewer && (
        <ImageLightbox
          images={viewer.images}
          startIndex={viewer.index}
          downloadName={`${project.name} photos`}
          onClose={() => setViewer(null)}
          labels={{
            close: t('common.close'),
            previous: t('feeds.previousPhoto'),
            next: t('feeds.nextPhoto'),
            openOriginal: t('feeds.openOriginal'),
            download: t('feeds.downloadPhoto'),
          }}
        />
      )}
    </div>
  );
};

export default ClientProjectPage;
